import { useMemo } from 'react';
import { t } from '@lingui/macro';

import Panel from '../../page-framework/panel/PanelDecorator';
import { PANEL_CATEGORIES, LINE_CHART_PANEL_PREVIEW_IMAGE } from '../../page-framework/panel/PanelRegistry';
import { panelPropTypes, panelPropDefaults } from '../../page-framework/panel/PanelPropTypes';
import usePanelDataService from '../../../services/PanelDataService';
import { useMetricPattern } from '../../charts/performance-chart/funcs';
import PerformanceChart from '../../charts/performance-chart/PerformanceChart';
import { BuiltInTooltip } from '../../charts/tooltip/types';
import { FORMATTER_TYPES } from '../../../utilities/FormattingUtilities';
import { networkDeviceFilterId, timeRangeFilterId } from '../../filters';

const title = t`Network Device CPU Utilization`;

const dataSource = {
    url: '/query/chart',
    params: {
        type: 'device',
        metricPatterns: ['cpu:usage:'],
        metrics: ['statusCpuUtil'],
        time: true,
    },
};

const metricPatternMapping = [
    {
        pattern: 'cpu:usage:',
        trendOptions: {
            formatter: FORMATTER_TYPES.PERCENT,
            units: '%',
            forceDecimals: true,
        },
        capitalize: true,
    },
];

const networkDeviceCpuUtilChartConfig = {
    yAxis: [
        {
            title: {
                text: t`Utilization (%)`,
            },
            ceiling: 100,
        },
    ],
    tooltip: BuiltInTooltip,
    showGranularity: true,
    statusMetric: 'statusCpuUtil',
};

function NetworkDeviceCpuUtilizationPanel() {
    const data = usePanelDataService(dataSource);
    const firstData = data?.data?.[0];
    const chartTrends = useMetricPattern(metricPatternMapping, firstData);
    const chartDef = useMemo(() => {
        return { ...networkDeviceCpuUtilChartConfig, chartTrends };
    }, [chartTrends]);

    if (!data) return null;

    return <PerformanceChart chartDef={chartDef} data={firstData} />;
}

NetworkDeviceCpuUtilizationPanel.propTypes = panelPropTypes;

NetworkDeviceCpuUtilizationPanel.defaultProps = {
    ...panelPropDefaults,
    title,
    contentHeight: 175,
    minContentHeight: 175,
};

export default Panel({
    typeAlias: 'DeviceCPUTrendChart',
    catalogEntry: {
        category: PANEL_CATEGORIES.DEVICE,
        title,
        description: t`CPU utilization trend for a selected network device`,
        previewImage: LINE_CHART_PANEL_PREVIEW_IMAGE,
        ipm: true,
    },
    filters: {
        required: [timeRangeFilterId, networkDeviceFilterId],
        optional: [],
    },
})(NetworkDeviceCpuUtilizationPanel);
